import { useEffect, useMemo, useRef, useState } from "react";
import { View, StyleSheet, TextInput } from "react-native";
import { Text } from "react-native-paper";

import {
    BMI_CATEGORY_LABEL,
    type BmiCategory,
    ageFromDob,
    ageMonthsFromDob,
    assessBmi,
    buildBodyMetricsValue,
    checkMetricRange,
    computeBmi,
    heightToInches,
    metricRangesForAge,
    parseBodyMetrics
} from "./bmi-classify";
import type { QuestionComponentProps } from "./types";

import { useDraftRegistry } from "@/lib/DraftRegistry";
import { colors, radius, spacing } from "@/lib/theme";

/**
 * One card for the whole body-metrics picture: the nurse types weight and
 * height, the patient's age and gender come off the visit for context, and BMI
 * plus its clinical band update as she types.
 *
 * Age and gender are shown, and they DO enter the classification: BMI itself is
 * weight ÷ height² for everyone, but the band that number falls into is read
 * off the CDC BMI-for-age chart for a 2-19 patient and the WHO adult cutoffs
 * for everyone else. Both are stored on the answer so the visit keeps its own
 * record even if the patient chart changes later — a percentile read against a
 * different age is a different clinical finding.
 *
 * Edit model is NumberQuestion's, not BloodPressureQuestion's, because this
 * field replaces a NumberQuestion (Q87) and inherits its problems: three local
 * drafts, committed to `onChange` on blur / unmount / registry-flush, never on
 * each keystroke. Committing per keystroke would run the section's `setAnswer`
 * autosave and `dropUnselectedAiSuggestions()` once per typed character — so
 * entering "232" would fire three saves and discard pending AI suggestions
 * three times. BMI and the range warnings still read the live draft, so the
 * card updates as she types; only the persisted answer waits for blur.
 */

const CATEGORY_STYLES: Record<BmiCategory, { bg: string; text: string; badge: string }> = {
    underweight: { bg: colors.info50, text: colors.info600, badge: colors.info500 },
    normal: { bg: colors.success50, text: colors.success600, badge: colors.success500 },
    overweight: { bg: colors.warning50, text: colors.warning600, badge: colors.warning500 },
    obese_1: { bg: colors.warning50, text: colors.warning600, badge: colors.warning600 },
    obese_2: { bg: colors.error50, text: colors.error600, badge: colors.error500 },
    obese_3: { bg: colors.error50, text: colors.error600, badge: colors.error600 },
    obesity: { bg: colors.warning50, text: colors.warning600, badge: colors.warning600 },
    severe_obesity: { bg: colors.error50, text: colors.error600, badge: colors.error600 }
};

/** "45th", "1st", "22nd" — the percentile reads as a rank, not a raw number. */
function ordinal(n: number): string {
    const rounded = Math.round(n);
    const mod100 = rounded % 100;
    if (mod100 >= 11 && mod100 <= 13) {
        return `${rounded}th`;
    }
    switch (rounded % 10) {
        case 1:
            return `${rounded}st`;
        case 2:
            return `${rounded}nd`;
        case 3:
            return `${rounded}rd`;
        default:
            return `${rounded}th`;
    }
}

/**
 * How the percentile reads under the BMI band.
 *
 * The tails are worded rather than numbered: a child four standard deviations
 * out rounds to "0th"/"100th", which reads like a broken calculation instead of
 * the extreme finding it is.
 */
function percentileNote(percentile: number): string {
    const scale = "for age and sex (CDC BMI-for-age)";
    if (percentile < 1) {
        return `Below the 1st percentile ${scale}`;
    }
    if (percentile > 99) {
        return `Above the 99th percentile ${scale}`;
    }
    return `${ordinal(percentile)} percentile ${scale}`;
}

export function BodyMetricsQuestion({
    question,
    value,
    onChange,
    readOnly,
    onFocus,
    onBlur,
    patient
}: QuestionComponentProps) {
    const stored = parseBodyMetrics(value);

    // Local edit buffers — see the note above on why this is not a controlled
    // per-keystroke field.
    const [weightLbs, setWeightLbs] = useState(stored.weightLbs);
    const [heightFt, setHeightFt] = useState(stored.heightFt);
    const [heightIn, setHeightIn] = useState(stored.heightIn);

    // Re-sync from the server (section hydration) only while the nurse is not
    // typing, so a late-arriving response cannot clobber her keystrokes.
    const editingRef = useRef(false);
    useEffect(() => {
        if (!editingRef.current) {
            const next = parseBodyMetrics(value);
            setWeightLbs(next.weightLbs);
            setHeightFt(next.heightFt);
            setHeightIn(next.heightIn);
        }
    }, [value]);

    // Prefer live patient data; fall back to whatever was stored with the
    // answer so a previously-saved card still reads correctly if the patient
    // record is unavailable.
    const ageYears = useMemo(
        () => ageFromDob(patient?.dob, new Date()) ?? stored.ageYears,
        [patient?.dob, stored.ageYears]
    );
    // Months drive the CDC lookup — the chart moves fast enough in early
    // childhood that rounding a 71-month-old down to "5" can shift a borderline
    // child across a band.
    const ageMonths = useMemo(
        () =>
            ageMonthsFromDob(patient?.dob, new Date()) ??
            stored.ageMonths ??
            (stored.ageYears != null ? Math.round(stored.ageYears * 12) : null),
        [patient?.dob, stored.ageMonths, stored.ageYears]
    );
    const gender = patient?.gender ?? stored.gender;

    // Mutable mirror so the unmount cleanup and the registry flush — both
    // registered once at mount — always see the latest drafts rather than a
    // stale mount-time closure. Same pattern as NumberQuestion's stateRef.
    const stateRef = useRef({
        weightLbs,
        heightFt,
        heightIn,
        ageYears,
        ageMonths,
        gender,
        value,
        onChange
    });
    stateRef.current = {
        weightLbs,
        heightFt,
        heightIn,
        ageYears,
        ageMonths,
        gender,
        value,
        onChange
    };

    const flushIfDirty = () => {
        const s = stateRef.current;
        const prev = parseBodyMetrics(s.value);
        // Dirty on the nurse's own three inputs only. Age/gender ride along
        // from the patient record and must not by themselves mark the card
        // dirty, or every render of an untouched question would write.
        const dirty =
            prev.weightLbs !== s.weightLbs ||
            prev.heightFt !== s.heightFt ||
            prev.heightIn !== s.heightIn;
        if (!dirty) {
            return;
        }
        s.onChange(
            buildBodyMetricsValue({
                weightLbs: s.weightLbs,
                heightFt: s.heightFt,
                heightIn: s.heightIn,
                ageYears: s.ageYears ?? null,
                ageMonths: s.ageMonths ?? null,
                gender: s.gender ?? null
            })
        );
    };
    const flushRef = useRef(flushIfDirty);
    flushRef.current = flushIfDirty;

    useEffect(() => {
        return () => flushRef.current();
    }, []);

    // The section's Complete handler flushes every registered draft before it
    // validates. Without this, tapping Complete straight from the keyboard
    // would validate a card the nurse has visibly filled in as empty.
    const registry = useDraftRegistry();
    const questionIdStr = String(question.id);
    useEffect(() => {
        registry.register(questionIdStr, { flushIfDirty: () => flushRef.current() });
        return () => registry.unregister(questionIdStr);
    }, [registry, questionIdStr]);

    const handleFocus = () => {
        editingRef.current = true;
        onFocus?.();
    };
    const handleBlur = () => {
        editingRef.current = false;
        flushRef.current();
        onBlur?.();
    };

    const inches = heightToInches(heightFt, heightIn);
    const bmi = useMemo(() => computeBmi(weightLbs, inches), [weightLbs, inches]);
    const assessment = useMemo(
        () => assessBmi({ bmi, ageMonths, gender }),
        [bmi, ageMonths, gender]
    );
    const category = assessment.category;

    // Bands read against THIS patient's age. A 43-lb five-year-old sits inside
    // the CDC 3rd-97th band for her age and draws no warning at all; the same
    // number judged against the adult band produced the "outside the possible
    // range (50-500)" line nurses were seeing on every pediatric visit.
    const ranges = useMemo(() => metricRangesForAge(ageYears), [ageYears]);
    const weightWarning = checkMetricRange(weightLbs, ranges.weight, "Weight");
    const heightWarning =
        inches === null ? null : checkMetricRange(String(inches), ranges.height, "Height");

    const contextParts = [
        ageYears != null ? `Age ${ageYears}` : null,
        gender ? gender.charAt(0).toUpperCase() + gender.slice(1) : null
    ].filter(Boolean);

    // decimal-pad + inputMode="decimal", matching the NumberQuestion this
    // replaces: a real weight can carry a decimal, and the plain "numeric"
    // keypad offers punctuation the field cannot use.
    const inputProps = {
        editable: !readOnly,
        placeholder: "—",
        placeholderTextColor: colors.textTertiary,
        keyboardType: "decimal-pad" as const,
        inputMode: "decimal" as const,
        onFocus: handleFocus,
        onBlur: handleBlur
    };

    return (
        <View>
            {contextParts.length > 0 ? (
                <Text style={styles.context}>{contextParts.join("  ·  ")}</Text>
            ) : null}

            <View style={styles.row}>
                <View style={styles.field}>
                    <Text style={styles.fieldLabel}>Weight</Text>
                    <View style={styles.inputRow}>
                        <TextInput
                            {...inputProps}
                            value={weightLbs}
                            onChangeText={(v) => {
                                editingRef.current = true;
                                setWeightLbs(v);
                            }}
                            style={styles.input}
                        />
                        <Text style={styles.unit}>lbs</Text>
                    </View>
                </View>

                <View style={styles.field}>
                    <Text style={styles.fieldLabel}>Height</Text>
                    <View style={styles.inputRow}>
                        <TextInput
                            {...inputProps}
                            value={heightFt}
                            onChangeText={(v) => {
                                editingRef.current = true;
                                setHeightFt(v);
                            }}
                            style={styles.inputNarrow}
                        />
                        <Text style={styles.unit}>ft</Text>
                        <TextInput
                            {...inputProps}
                            value={heightIn}
                            onChangeText={(v) => {
                                editingRef.current = true;
                                setHeightIn(v);
                            }}
                            style={styles.inputNarrow}
                        />
                        <Text style={styles.unit}>in</Text>
                    </View>
                </View>
            </View>

            {weightWarning ? <Text style={styles.warning}>{weightWarning}</Text> : null}
            {heightWarning ? <Text style={styles.warning}>{heightWarning}</Text> : null}

            {bmi !== null && category ? (
                <View style={styles.result}>
                    <View
                        style={[styles.stripe, { backgroundColor: CATEGORY_STYLES[category].bg }]}
                    >
                        <Text
                            style={[
                                styles.badge,
                                { backgroundColor: CATEGORY_STYLES[category].badge }
                            ]}
                        >
                            BMI {bmi}
                        </Text>
                        <Text
                            style={[styles.stripeText, { color: CATEGORY_STYLES[category].text }]}
                        >
                            {BMI_CATEGORY_LABEL[category]}
                        </Text>
                    </View>
                    {assessment.scale === "pediatric" && assessment.percentile != null ? (
                        <Text style={styles.scaleNote}>
                            {percentileNote(assessment.percentile)}
                        </Text>
                    ) : null}
                </View>
            ) : bmi !== null && assessment.reason ? (
                // A real BMI we decline to classify — an infant, or a child with
                // no sex on file. Show the number and say why there is no band,
                // rather than showing an adult band that does not apply.
                <View style={styles.result}>
                    <View style={[styles.stripe, { backgroundColor: colors.gray100 }]}>
                        <Text style={[styles.badge, { backgroundColor: colors.gray500 }]}>
                            BMI {bmi}
                        </Text>
                        <Text style={[styles.stripeText, { color: colors.gray600 }]}>
                            Not classified
                        </Text>
                    </View>
                    <Text style={styles.scaleNote}>{assessment.reason}</Text>
                </View>
            ) : (
                <Text style={styles.pending}>Enter weight and height to calculate BMI.</Text>
            )}
        </View>
    );
}

const styles = StyleSheet.create({
    context: {
        fontSize: 12,
        color: colors.gray500,
        marginBottom: spacing.sm
    },
    row: {
        flexDirection: "row",
        gap: spacing.base,
        flexWrap: "wrap"
    },
    field: {
        gap: spacing.xs
    },
    fieldLabel: {
        fontSize: 11,
        letterSpacing: 0.4,
        textTransform: "uppercase",
        color: colors.gray500
    },
    inputRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.sm
    },
    input: {
        backgroundColor: colors.surface,
        borderWidth: 1,
        borderColor: colors.border,
        borderRadius: radius.xs,
        paddingVertical: 10,
        paddingHorizontal: 14,
        fontSize: 20,
        color: colors.ink,
        width: 96
    },
    inputNarrow: {
        backgroundColor: colors.surface,
        borderWidth: 1,
        borderColor: colors.border,
        borderRadius: radius.xs,
        paddingVertical: 10,
        paddingHorizontal: 14,
        fontSize: 20,
        color: colors.ink,
        width: 72
    },
    unit: {
        fontSize: 13,
        color: colors.gray500
    },
    warning: {
        fontSize: 12,
        color: colors.warning600,
        marginTop: spacing.sm
    },
    stripe: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.sm,
        borderRadius: radius.xs,
        padding: spacing.sm,
        marginTop: spacing.md
    },
    badge: {
        color: colors.surface,
        fontSize: 12,
        fontWeight: "700",
        letterSpacing: 0.4,
        paddingHorizontal: spacing.sm,
        paddingVertical: 3,
        borderRadius: 5,
        overflow: "hidden"
    },
    stripeText: {
        flex: 1,
        fontSize: 13,
        fontWeight: "600"
    },
    pending: {
        fontSize: 12,
        color: colors.gray400,
        marginTop: spacing.md,
        fontStyle: "italic"
    },
    result: {
        gap: spacing.xs
    },
    scaleNote: {
        fontSize: 11,
        color: colors.gray500,
        paddingHorizontal: spacing.xs
    }
});
